import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, CircularProgress, TextField, InputAdornment, Autocomplete, Dialog, DialogContent, IconButton } from "@mui/material";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";

import axiosConfig from "../../axios/axiosConfig";
import ReactHotToaster from "../../components/common/ReactHotToaster";

import LocationOnIcon from "@mui/icons-material/LocationOn";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";

// ── palette (brand) ──────────────────────────────────────────────────────
const pageBg = "#eef2f7";
const cardBg = "#ffffff";
const cardBorder = "#e2e8f0";
const navy = "#04343a";
const brandGreen = "#016551";
const textMain = "#1e293b";
const textMuted = "#64748b";

const LOCATION_COLUMNS = ["Latitude", "Longitude", "IP Address", "Location", "Date & Time"];

const isMapLink = (location) => typeof location === "string" && location.startsWith("http");

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d.getTime())
    ? value
    : d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

// Google Maps embeds a plain "maps?q=" link only via the undocumented
// output=embed param — no API key needed for a single-pin view.
const toEmbedUrl = (latitude, longitude) => `https://maps.google.com/maps?q=${latitude},${longitude}&z=15&output=embed`;

const LocationHistoryPage = () => {
  const [advisorsLoading, setAdvisorsLoading] = useState(true);
  const [advisors, setAdvisors] = useState([]);
  const [selectedAdvisor, setSelectedAdvisor] = useState(null);

  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [search, setSearch] = useState("");
  const [mapDialog, setMapDialog] = useState(null); // { latitude, longitude, link }

  const loadAdvisors = async () => {
    setAdvisorsLoading(true);
    try {
      const res = await axiosConfig({
        method: "get",
        url: "/tnibroadmin/advisors-list",
      });
      setAdvisors(Array.isArray(res.data?.response) ? res.data.response : []);
    } catch (error) {
      console.log("advisors-list error: ", error);
      toast.error(error?.response?.data?.response || "Failed to load advisor list");
    } finally {
      setAdvisorsLoading(false);
    }
  };

  const loadLocationHistory = async (userId) => {
    setHistoryLoading(true);
    try {
      const res = await axiosConfig({
        method: "get",
        url: "/tnibroadmin/advisor-location-ip-history",
        params: { user_id: userId },
      });
      setHistoryRows(Array.isArray(res.data?.response) ? res.data.response : []);
    } catch (error) {
      console.log("advisor-location-ip-history error: ", error);
      toast.error(error?.response?.data?.response || "Failed to load location history");
      setHistoryRows([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    loadAdvisors();
  }, []);

  useEffect(() => {
    if (selectedAdvisor?.id) {
      loadLocationHistory(selectedAdvisor.id);
    } else {
      setHistoryRows([]);
    }
  }, [selectedAdvisor]);

  const filteredHistory = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return historyRows;
    return historyRows.filter((r) =>
      Object.values(r || {}).some((v) => String(v ?? "").toLowerCase().includes(q))
    );
  }, [historyRows, search]);

  const handleDownload = () => {
    if (!selectedAdvisor) {
      toast.error("Please select an advisor first");
      return;
    }
    if (!filteredHistory.length) {
      toast.error("No location history to download");
      return;
    }
    const rows = filteredHistory.map((r) => ({
      Latitude: r.latitude ?? "—",
      Longitude: r.longitude ?? "—",
      "IP Address": r.ip ?? "—",
      Location: r.location ?? "—",
      "Date & Time": formatDateTime(r.created_date_time),
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Location History");
    XLSX.writeFile(workbook, `location-history-${selectedAdvisor.username}-${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("Downloaded successfully!");
  };

  return (
    <Box sx={{ p: 3, background: pageBg, minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          background: navy,
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
          p: "20px 24px",
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 44, height: 44, borderRadius: "10px", background: "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <LocationOnIcon sx={{ color: "#1db954", fontSize: 24 }} />
          </Box>
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 20, lineHeight: 1.2 }}>
              Location History
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 12, mt: "2px" }}>
              Advisor login IP &amp; location history
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <Box
            component="button"
            onClick={() => selectedAdvisor && loadLocationHistory(selectedAdvisor.id)}
            sx={{
              display: "flex", alignItems: "center", gap: 0.75,
              background: "rgba(255,255,255,0.1)", color: "#fff", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px",
              padding: "9px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer",
              "&:hover": { background: "rgba(255,255,255,0.18)" },
            }}
          >
            <RefreshIcon sx={{ fontSize: 16 }} /> Refresh
          </Box>
          <Box
            component="button"
            onClick={handleDownload}
            sx={{
              display: "flex", alignItems: "center", gap: 0.75,
              background: brandGreen, color: "#fff", border: "none", borderRadius: "8px",
              padding: "9px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer",
              boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
              "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)", boxShadow: "0 6px 16px rgba(1,101,81,0.4)" },
            }}
          >
            <DownloadIcon sx={{ fontSize: 16 }} /> Download Excel
          </Box>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <Autocomplete
          size="small"
          loading={advisorsLoading}
          options={advisors}
          value={selectedAdvisor}
          onChange={(e, value) => setSelectedAdvisor(value)}
          getOptionLabel={(option) => (option?.username ? `${option.username} (${option.id})` : "")}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          sx={{ background: cardBg, borderRadius: "8px", minWidth: 280 }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select advisor"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: textMuted }} />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
        <TextField
          size="small"
          placeholder="Search in results"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={!selectedAdvisor}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: textMuted }} />
              </InputAdornment>
            ),
          }}
          sx={{ background: cardBg, borderRadius: "8px", minWidth: 280 }}
        />
      </Box>

      {/* Table */}
      <Box sx={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: "12px", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
            <Box component="thead">
              <Box component="tr">
                {LOCATION_COLUMNS.map((h) => (
                  <Box
                    component="th"
                    key={h}
                    sx={{
                      textAlign: "left", fontSize: 11, color: "#fff", fontWeight: 700,
                      textTransform: "uppercase", letterSpacing: "0.5px",
                      background: navy, p: "12px 18px", whiteSpace: "nowrap",
                    }}
                  >
                    {h}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {!selectedAdvisor ? (
                <Box component="tr">
                  <Box component="td" colSpan={LOCATION_COLUMNS.length} sx={{ p: 4, textAlign: "center", fontSize: 13, color: textMuted }}>
                    Select an advisor to view their location history
                  </Box>
                </Box>
              ) : historyLoading ? (
                <Box component="tr">
                  <Box component="td" colSpan={LOCATION_COLUMNS.length} sx={{ p: 4, textAlign: "center" }}>
                    <CircularProgress size={26} sx={{ color: brandGreen }} />
                  </Box>
                </Box>
              ) : filteredHistory.length === 0 ? (
                <Box component="tr">
                  <Box component="td" colSpan={LOCATION_COLUMNS.length} sx={{ p: 4, textAlign: "center", fontSize: 13, color: textMuted }}>
                    No location history found
                  </Box>
                </Box>
              ) : (
                filteredHistory.map((r, i) => (
                  <Box component="tr" key={i} sx={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
                    <Box component="td" sx={{ p: "10px 18px", fontSize: 12.5, color: textMain, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
                      {r.latitude ?? "—"}
                    </Box>
                    <Box component="td" sx={{ p: "10px 18px", fontSize: 12.5, color: textMain, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
                      {r.longitude ?? "—"}
                    </Box>
                    <Box component="td" sx={{ p: "10px 18px", fontSize: 12.5, color: textMain, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
                      {r.ip ?? "—"}
                    </Box>
                    <Box component="td" sx={{ p: "10px 18px", fontSize: 12.5, borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
                      {isMapLink(r.location) ? (
                        <Box
                          component="span"
                          onClick={() => setMapDialog({ latitude: r.latitude, longitude: r.longitude, link: r.location })}
                          sx={{
                            display: "inline-flex", alignItems: "center", gap: 0.5,
                            color: brandGreen, fontWeight: 700, cursor: "pointer",
                            textDecoration: "underline",
                            "&:hover": { color: "#014d3d" },
                          }}
                        >
                          View on map <OpenInNewIcon sx={{ fontSize: 14 }} />
                        </Box>
                      ) : (
                        <Typography component="span" sx={{ fontSize: 12.5, color: textMuted }}>
                          {r.location || "—"}
                        </Typography>
                      )}
                    </Box>
                    <Box component="td" sx={{ p: "10px 18px", fontSize: 12, color: textMuted, borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
                      {formatDateTime(r.created_date_time)}
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Map modal */}
      <Dialog open={!!mapDialog} onClose={() => setMapDialog(null)} maxWidth="sm" fullWidth>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: navy, p: "12px 16px" }}>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>Advisor Location</Typography>
          <IconButton size="small" onClick={() => setMapDialog(null)} sx={{ color: "#fff" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 0 }}>
          {mapDialog && (
            <Box
              component="iframe"
              title="advisor-location-map"
              src={toEmbedUrl(mapDialog.latitude, mapDialog.longitude)}
              sx={{ width: "100%", height: 420, border: "none", display: "block" }}
            />
          )}
        </DialogContent>
      </Dialog>

      <ReactHotToaster />
    </Box>
  );
};

export default LocationHistoryPage;
